import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { useHistory } from 'react-router-dom';
import { MagnifyingGlassIcon, PlusIcon, KeyIcon, BookOpenIcon, FolderIcon, LifebuoyIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { FolderSearch } from 'lucide-react';
import { get, ref } from 'firebase/database';

import { db } from '../../utils/firebaseApp.js';
import { AuthContext } from '../../utils/auth.js';
import { navigation, signOut } from './Sidebar.js';
import cn from '../../ui/cn.js';
import Kbd from '../../ui/Kbd.js';

function score(query, text) {
  const q = query.trim().toLowerCase();
  const t = String(text || '').toLowerCase();
  if (!q) return 1;
  if (t === q) return 100;
  if (t.startsWith(q)) return 60;
  if (t.includes(q)) return 30;
  // loose subsequence match
  let i = 0;
  for (const ch of t) if (ch === q[i]) i += 1;
  return i === q.length ? 10 : 0;
}

/**
 * CommandPalette (⌘K): navigate, run quick actions and jump straight to an
 * extraction or classification by name.
 */
export default function CommandPalette({ open, onClose }) {
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);
  const [query, setQuery] = useState('');
  const [resources, setResources] = useState({ extractions: [], classifications: [], loaded: false });

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    if (resources.loaded || !currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const [ex, cl] = await Promise.all([
          get(ref(db, `extractions/${currentUser.uid}`)),
          get(ref(db, `document_classification/classifications/${currentUser.uid}`)),
        ]);
        if (cancelled) return;
        const toList = (snap) =>
          snap.exists()
            ? Object.entries(snap.val())
                .map(([id, value]) => ({ id, name: value?.name || 'Untitled', createdAt: Number(value?.createdAt) || 0 }))
                .sort((a, b) => b.createdAt - a.createdAt)
            : [];
        setResources({ extractions: toList(ex), classifications: toList(cl), loaded: true });
      } catch (error) {
        if (!cancelled) setResources((r) => ({ ...r, loaded: true }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentUser?.uid, resources.loaded]);

  const groups = useMemo(() => {
    const nav = navigation.map((item) => ({ id: `nav-${item.key}`, label: item.name, hint: item.to, icon: item.icon, to: item.to, group: 'Go to' }));
    const actions = [
      { id: 'new-extraction', label: 'New extraction', hint: 'Create an extraction template', icon: PlusIcon, to: '/extractions?new=1', group: 'Actions' },
      { id: 'new-classification', label: 'New classification', hint: 'Create a classification', icon: PlusIcon, to: '/classifications?new=1', group: 'Actions' },
      { id: 'api-key', label: 'Create API key', hint: 'Open the API page', icon: KeyIcon, to: '/api?create=1', group: 'Actions' },
      { id: 'buy-pages', label: 'Buy pages', hint: 'Top up your balance', icon: PlusIcon, to: '/billing', group: 'Actions' },
      { id: 'docs', label: 'API documentation', hint: 'docs.extracta.ai', icon: BookOpenIcon, href: 'https://docs.extracta.ai', group: 'Help' },
      { id: 'support', label: 'Contact support', hint: 'office@extracta.ai', icon: LifebuoyIcon, href: 'mailto:office@extracta.ai', group: 'Help' },
      { id: 'logout', label: 'Log out', hint: 'Sign out of Extracta LABS', icon: ArrowRightOnRectangleIcon, action: signOut, group: 'Account' },
    ];
    const ex = resources.extractions.map((e) => ({ id: `ex-${e.id}`, label: e.name, hint: 'Extraction', icon: FolderIcon, to: `/extractions/${e.id}`, group: 'Extractions' }));
    const cl = resources.classifications.map((c) => ({ id: `cl-${c.id}`, label: c.name, hint: 'Classification', icon: FolderSearch, to: `/classifications/${c.id}`, group: 'Classifications' }));

    const all = [...nav, ...actions, ...ex, ...cl];
    const q = query.trim();
    const filtered = q
      ? all
          .map((item) => ({ item, s: Math.max(score(q, item.label), score(q, item.hint) * 0.5) }))
          .filter(({ s }) => s > 0)
          .sort((a, b) => b.s - a.s)
          .map(({ item }) => item)
          .slice(0, 24)
      : [...nav, ...actions.slice(0, 4), ...ex.slice(0, 5), ...cl.slice(0, 3)];

    const order = ['Go to', 'Actions', 'Extractions', 'Classifications', 'Help', 'Account'];
    return order.map((group) => ({ group, items: filtered.filter((i) => i.group === group) })).filter((g) => g.items.length);
  }, [query, resources]);

  const select = (item) => {
    if (!item) return;
    onClose();
    if (item.action) item.action();
    else if (item.href) window.open(item.href, '_blank', 'noopener,noreferrer');
    else if (item.to) history.push(item.to);
  };

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[80]" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-[0.98]" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-[0.98]">
            <Dialog.Panel className="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 transition-all">
              <Combobox onChange={select}>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                  <Combobox.Input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search pages, extractions, actions…"
                    onChange={(event) => setQuery(event.target.value)}
                    autoComplete="off"
                  />
                </div>

                {groups.length > 0 ? (
                  <Combobox.Options static className="max-h-[60vh] scroll-py-2 overflow-y-auto p-2">
                    {groups.map(({ group, items }) => (
                      <li key={group} className="mb-1 last:mb-0">
                        <h2 className="px-2.5 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-gray-400">{group}</h2>
                        <ul>
                          {items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Combobox.Option key={item.id} value={item} className={({ active }) => cn('flex cursor-default select-none items-center gap-3 rounded-lg px-2.5 py-2', active ? 'bg-indigo-600 text-white' : 'text-gray-700')}>
                                {({ active }) => (
                                  <>
                                    <span className={cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md', active ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-500')}>
                                      <Icon className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className={cn('block truncate text-sm font-medium', active ? 'text-white' : 'text-gray-900')}>{item.label}</span>
                                      {item.hint && <span className={cn('block truncate text-xs', active ? 'text-indigo-100' : 'text-gray-500')}>{item.hint}</span>}
                                    </span>
                                    {active && <span className="text-xs text-indigo-100">↵</span>}
                                  </>
                                )}
                              </Combobox.Option>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </Combobox.Options>
                ) : (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">No results for “{query}”</p>
                    <p className="mt-1 text-sm text-gray-500">Try a page name, an extraction or an action.</p>
                  </div>
                )}

                <div className="flex items-center gap-4 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigate</span>
                  <span className="flex items-center gap-1"><Kbd>↵</Kbd> open</span>
                  <span className="flex items-center gap-1"><Kbd>esc</Kbd> close</span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
